import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const primaryLinks = [
  { name: 'Home', path: '/' },
  { name: 'Scores', path: '/scores' },
  { name: 'Commanders', path: '/commanders' },
  { name: 'Nationals', path: '/nationals' },
  { name: 'Capitals', path: '/capitals' },
  { name: 'Wizards', path: '/wizards' },
  { name: 'Mystics', path: '/mystics' },
  { name: 'DC United', path: '/dcunited' },
  { name: 'Podcast', path: '/podcast' },
];

const moreLinks = [
  { name: 'DC Soccer', path: '/dcsoccer' },
  { name: 'Defenders', path: '/defenders' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header data-testid="main-navbar" className="sticky top-0 z-50 bg-[#002868] border-b-2 border-[#BF0A30]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0 hover-lift">
            <img
              src="https://customer-assets.emergentagent.com/job_dcsportshub/artifacts/fw8w9adx_DCSportsReport_Logo.png"
              alt="DC Sports Report"
              className="h-11 w-auto"
            />
            <span className="hidden xl:block font-heading text-white text-lg uppercase tracking-wide leading-none">
              DC Sports Report
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center">
            {primaryLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`relative px-3 py-5 text-sm font-heading font-medium uppercase tracking-wide transition-colors ${
                  isActive(link.path)
                    ? 'text-white after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:bg-[#BF0A30]'
                    : 'text-white/75 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-more-menu"
                className="flex items-center gap-1 px-3 py-5 text-sm font-heading font-medium uppercase tracking-wide text-white/75 hover:text-white transition-colors outline-none"
              >
                More
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-slate-200 bg-white">
                {moreLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild className="rounded-none">
                    <Link
                      to={link.path}
                      data-testid={`nav-more-link-${link.name.toLowerCase().replace(' ', '-')}`}
                      className="w-full font-heading uppercase tracking-wide text-sm text-slate-800 cursor-pointer"
                    >
                      {link.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div data-testid="mobile-menu" className="lg:hidden py-3 border-t border-white/15">
            <div className="flex flex-col">
              {[...primaryLinks, ...moreLinks].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setIsOpen(false)}
                  className={`px-3 py-3 text-sm font-heading uppercase tracking-wide border-l-2 transition-colors ${
                    isActive(link.path)
                      ? 'text-white border-[#BF0A30] bg-white/5'
                      : 'text-white/75 border-transparent hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
